<template>
  <div class="c-tooltip" :class="classes" :style="style">
    <div class="c-tooltip-body">
      <component :is="info.component" v-bind="info.props"/>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { asPixels } from '@/shared/utils/style';
  import { TooltipInfo } from '../../models/TooltipInfo';

  /**
   * Distance to position tooltips from the left edge of large targets.
   */
  const LARGE_TARGET_OFFSET = 12;

  /**
   * Minimum width for tooltip targets to be considered as large.
   */
  const LARGE_TARGET_WIDTH = 96;

  /**
   * Distance from the bottom of the window where the tooltip should be shown before the target.
   */
  const BOTTOM_ZONE = 300;

  const props = defineProps<{
    info: TooltipInfo;
  }>();

  const targetRect = computed(() => props.info.target.getBoundingClientRect());

  const anchorPosition = computed<{ top: boolean, left: boolean }>(() => {
    const left = targetRect.value.left < (window.innerWidth / 2);
    const top = targetRect.value.bottom > (window.innerHeight - BOTTOM_ZONE);
    return { top, left };
  });

  const classes = computed(() => ({
    [`c-tooltip-${props.info.theme}`]: true,
    'c-tooltip-left': anchorPosition.value.left,
    'c-tooltip-right': !anchorPosition.value.left,
    'c-tooltip-top': anchorPosition.value.top,
    'c-tooltip-bottom': !anchorPosition.value.top,
  }));

  const style = computed(() => {
    const r = targetRect.value;
    return {
      left: asPixels(r.left + (r.width >= LARGE_TARGET_WIDTH ? LARGE_TARGET_OFFSET : r.width / 2)),
      ...(anchorPosition.value.top ? { bottom: asPixels(window.innerHeight - r.top) } : { top: asPixels(r.bottom) }),
    };
  });
</script>

<style lang="scss">
  .c-tooltip {
    --bg-color: #{rgba($neutral-1000, 0.8)};
    --fg-color: #{$neutral-0};

    $pointer-size: .5rem;
    $pointer-margin: 2px;
    $body-offset: -.75rem;

    position: fixed;
    z-index: $tooltip-layer;
    width: $pointer-size;
    height: $pointer-size;
    margin-left: -$pointer-size*0.5;
    border-left: $pointer-size*0.5 solid transparent;
    border-right: $pointer-size*0.5 solid transparent;

    &-body {
      background-color: var(--bg-color);
      color: var(--fg-color);
      border-radius: $border-radius;
      position: absolute;
      width: max-content;
      max-width: 20em;
      padding: $card-padding;

      ul {
        margin-left: 1.25em;
        list-style-type: disc;
      }

      p {
        white-space: pre-line;
      }
    }

    &-bottom {
      margin-top: $pointer-margin;
      border-bottom: $pointer-size solid var(--bg-color);

      .c-tooltip-body {
        top: $pointer-size;
      }
    }

    &-top {
      margin-bottom: $pointer-margin;
      border-top: $pointer-size solid var(--bg-color);

      .c-tooltip-body {
        bottom: $pointer-size;
      }
    }

    &-left {
      .c-tooltip-body {
        left: $body-offset;
      }
    }

    &-right {
      .c-tooltip-body {
        right: $body-offset;
      }
    }

    &-neutral {
      --bg-color: #{$neutral-0};
      --fg-color: inherit;

      filter: drop-shadow(1px 1px 10px rgba($neutral-1000, 0.2));
    }
  }
</style>
